import { CommonRegistry } from '@common/components/components';

import { MOVEMENT_CONFIG } from '../config';
import { World } from '../World';

/**
 * Service for calculating entity speed and animation scaling factors.
 * Centralises the logic for MovementSpeeds and SpeedMultiplier components.
 */
export class SpeedService {
  /**
   * Calculates the final movement speed for an entity.
   * Logic: (MovementSpeeds[mode]) * (SpeedMultiplier.multiplier ?? 1)
   */
  public getEffectiveSpeed(world: World<CommonRegistry>, entityId: number): number {
    const speeds = world.getComponent(entityId, 'MovementSpeeds');
    const mode = world.getComponent(entityId, 'MovementMode');
    const speedMultiplier = world.getComponent(entityId, 'SpeedMultiplier');

    if (!speeds || !mode) {
      return 0;
    }

    const baseSpeed = mode.mode === 'walk' ? speeds.walk : speeds.run;
    const multiplier = speedMultiplier?.multiplier ?? 1;

    return baseSpeed * multiplier;
  }

  /**
   * Calculates the scale factor for animation playback speed.
   * Logic: effectiveSpeed / MOVEMENT_CONFIG.ANIMATION_BASELINE_SPEED
   */
  public getAnimationSpeedScale(world: World<CommonRegistry>, entityId: number): number {
    const hasSpeeds = world.hasComponent(entityId, 'MovementSpeeds');
    if (!hasSpeeds) {
      return 1;
    }

    const effectiveSpeed = this.getEffectiveSpeed(world, entityId);
    const baselineSpeed = MOVEMENT_CONFIG.ANIMATION_BASELINE_SPEED as number;

    if (baselineSpeed === 0) {
      // Prevent division by zero if config is corrupted/incorrect
      return 1;
    }

    return effectiveSpeed / baselineSpeed;
  }
}
